<template>

    <basic-card>
        <template #header>
            {{ headerLabel }}
        </template>

        <loader indefinite message="Loading operations" v-show="loading"/>
        <data-table
          forget-focused
          :key="operationDefinitionId"
          :data="operationsAdjusted"
          :pagination="pagination"
          :configuration="operationsTableConfig"
          @focusedChanged="handleFocusedChanged"
          @pageChanged="pageChanged"
          @sortChanged="sortChanged"
          v-show="!loading"
          :component-id="`operations[${operationDefinitionId}]-list`"
        />

        <v-modal :name="`operationDetailWidgetModal${operationDefinitionId}`"
                 style="background-color: rgba(10,10,10,0.76)">
            <loader indefinite message="Loading operations" v-show="loading"/>
            <operation-editor-layout v-show="!loading" v-if="focusedOperation != null"
                                     :key="focusedOperation.id"
                                     :operation="focusedOperation"
                                     @save="handleSave"
                                     @cancel="handleCancel"
            />
        </v-modal>


    </basic-card>


</template>

<script>
import { mapGetters } from "vuex";
import DataTable from "@/components/table/DataTable";
import OperationStore from "@/stores/operations-store";
import BasicCard from "@/components/layout/BasicCard";
import Loader from "@/components/Loader";
import OperationEditorLayout from "@/layout/OperationEditorLayout";

const modifiedStyles = {'font-style': 'italic', 'background-color': 'rgba(200,83,83,0.15)'};

export default {
    name: "OperationListWidget",
    components: {
        OperationEditorLayout,
        Loader,
        BasicCard,
        DataTable
    },
    props: {
        operationDefinitionId: String,
        operationDefinitionQuery: String,
        displayName: String,
    },
    data: function () {
        return {
            actualOperationDefinition: null,

            cachedOperations: {},

            query: {
                parameters: {}
            },

            operationsTableConfig: {
                columns: [
                    {
                        caption: this.$t('status'),
                        datum: d => d.actualStatus,
                        style: d => d.modified ? modifiedStyles : undefined
                    },
                    {
                        caption: "Id",
                        datum: d => d.id.substr(d.id.length - 5),
                        style: d => d.modified ? modifiedStyles : undefined
                    }
                ],

            },

            operationType: null, //ID of operation definiton

            actualPage: 0,
            pageSize: 20,
            cashEvents: [],
            focusedOperation: null,
            operationSaveError: Object,
            loading: false,

        }
    },
    computed: {
        ...mapGetters({
            getOperationDefinition: "operationDefinitions/operationDefinition",
            getOperationDefinitionQuery: "operationQueries/operationQueryById",
            account: "banking/account",
            party: "parties/party",
        }),

        operations() {
            return {
                actualItems: this.$store.getters[`${this.operationType}/getActualPageItems`],
                modifiedItems: this.$store.getters[`${this.operationType}/getCreatedItems`]
            }
        },
        operationsAdjusted() {
            return [
                ...this.operations.modifiedItems.map(op => ({...op, modified: true})),
                ...this.operations.actualItems.filter(item => !this.operations.modifiedItems.find(op => op.id == item.id))
                  .map(op => ({...op, modified: false}))
            ]
        },
        pagination() {
            return this.$store.getters[`${this.operationType}/getPaginationController`]
        },

        transformedQuery() {
            const resultObject = this.$_.cloneDeep(this.query);

            resultObject.parameters.operationDefinition = {
                operator: "eq",
                value: this.$props.operationDefinitionId,
                type: "string"
            };

            return resultObject;
        },
        headerLabel() {
            return this.displayName ?? this.query.name ?? this.actualOperationDefinition.name
        }

    },
    methods: {
        async pageChanged(page, query, pageSize) {
            this.actualPage = page;
            this.pageSize = pageSize;
            this.loading = true;
            await this.$store.dispatch(`${this.operationType}/refreshOperations`, {
                requestedPage: this.actualPage,
                size: this.pageSize,
                query: this.transformedQuery
            });
            this.loading = false;
        },

        sortChanged(sort) {
            this.loading = true;
            this.$store.commit(`${this.operationType}/clearCreatedOperations`);
            this.$store.dispatch(`${this.operationType}/refreshOperations`, {
                requestedPage: this.actualPage,
                query: this.transformedQuery,
                sort: sort
            }).then(() => this.loading = false);
        },

        handleFocusedChanged(focused) {
            this.focusedOperation = focused;
            this.operationSaveError = null
            if (focused != null) {
                this.$modal.show(`operationDetailWidgetModal${this.operationDefinitionId}`);
            }
        },

        handleCancel() {
            this.focusedOperation = null;
            this.operationSaveError = null
            this.$modal.hide(`operationDetailWidgetModal${this.operationDefinitionId}`);
        },
        async handleSave(savePayload) {

            try {

                const operation = savePayload.operation;
                const operationLinks = savePayload.links;

                this.operationSaving = true;
                const savedOperation = await this.$store.dispatch(`${this.operationType}/saveOperation`, operation)
                const linkDefinitions = Object.keys(operationLinks.primaryLinks);
                const currentOperationId = savedOperation.id;

                await Promise.all(linkDefinitions.map(linkDef => {
                    const targetOperationId = operationLinks.primaryLinks[linkDef];
                    return this.$store.dispatch("operationLinks/saveOperationLink", {
                        primarySide: targetOperationId,
                        secondarySide: currentOperationId,
                        linkDefinition: linkDef
                    })
                }))

                this.operationSaving = false;
                this.$modal.hide(`operationDetailWidgetModal${this.operationDefinitionId}`);
                this.$notify({
                    group: 'application',
                    title: this.$t('operation.saveSuccessTitle'),
                    text: this.$t('operation.saveSuccess', {saveSuccess: this.focusedOperation.id}),
                    type: "ok"
                });
                this.focusedOperation = null
                this.operationSaveError = null
            } catch (error) {
                console.warn(error)
                this.$notify({
                    group: 'application',
                    title: this.$t('operation.saveError'),
                    text: this.$t('operation.saveError'),
                    type: "error"
                });
            }
        },
    },
    watch: {
        'operationDefinitionId': {
            async handler(newValue) {
                if (newValue) {
                    this.loading = true
                    this.operationType = newValue;


                    if (typeof this.$store._modules.root._children[this.operationType] != 'undefined') {
                        this.$store.unregisterModule(this.operationType);
                    }

                    this.operationType = newValue;
                    this.actualOperationDefinition = this.getOperationDefinition(this.operationType);
                    this.$store.registerModule(this.operationType, OperationStore(this.operationType));

                    this.query = this.getOperationDefinitionQuery(this.operationDefinitionQuery) ?? {parameters: {}};
                    await this.pageChanged(0, this.transformedQuery, this.pageSize);

                }
            },
            immediate: true
        },

        actualOperationDefinition: {
            handler(newVal) {
                const defaultVal = par => d => d.content[par.name];

                const valueLambdas = {
                    STRING: defaultVal,
                    ENUM: par => d => {
                        let value = d.content[par.name];
                        let enumOption = par.details.enumValues.find(op => op.value == value);
                        if (enumOption == undefined) return value;
                        else return enumOption.name;
                    },
                    DATE: par => d => this.$formats.formatDate(defaultVal(par)(d), false),
                    DECIMAL: par => d => this.$numeral(d.content[par.name] / Math.pow(10, par.details.precision)).format('0,0.' + '0'.repeat(par.details.precision)),
                    INTEGER: par => d => this.$numeral(d.content[par.name]).format('0'),
                    BOOLEAN: par => d => d.content[par.name] ? this.$t("yes") : this.$t("no"),
                    GEO_POINT: par => d => {
                        if (!d.content[par.name]) {
                            return "N/A"
                        }
                        return `${this.$formats.formatAmount(d.content[par.name]?.longtitude)}/${this.$formats.formatAmount(d.content[par.name]?.latitude)}`;
                    },
                    ENTITY_REFERENCE: par => d => {
                        let ref_id = d.content[par.name];
                        if (par.details.entityType == 'account') {
                            let acc = this.account(ref_id) ?? "N/A";
                            return acc.name
                        }
                        if (par.details.entityType == 'party') {
                            let party = this.party(ref_id) ?? "N/A";
                            return party.displayName
                        }
                        if (par.details.entityType == 'operation') {
                            return this.cachedOperations[ref_id] ?? "N/A"
                        }
                        return ref_id;
                    },
                    DYNA_MODEL: par => d => "Complex Object {...}",
                    ARRAY: par => d => `${d.content[par.name].length} items`
                };

                const styleLambdas = {
                    STRING: undefined,
                    DATE: undefined,
                    DECIMAL: d => ({'text-align': 'right'}),
                    CURRENCY: undefined,
                }

                const operationCache = {};

                Object.keys(newVal.model.dynaProperties).map(key => newVal.model.dynaProperties[key]).forEach(par => {
                    if (par.details.type === 'ENTITY_REFERENCE') {
                        this.$store.dispatch("operations/fetchOperations", {
                            requestedPage: 0,
                            query: par.details.entityFilter,
                            sort: undefined,
                            size: 100000
                        })
                          .then(data => {
                              data.list.forEach(op => operationCache[op.id] = op.name ?? op.id)
                          })
                    }

                    this.operationsTableConfig.columns.push({
                        caption: par.displayName,
                        datum: valueLambdas[par.details.type](par),
                        style: d => ({
                            ...styleLambdas[par.details.type] ?? (d),
                            ...d.modified ? modifiedStyles : undefined
                        }),
                        sortField: `content.values.${par.name}`,
                        allowSort: true
                    })
                })

                this.$set(this, 'cachedOperations', operationCache);

            },
            immediate: true
        }
    },
}
</script>

<style scoped>

</style>
